import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { MODAL_VARIANTS } from "../../../../constants/animations";

/**
 * ModalContent - Reusable modal content wrapper with scale/fade animation
 * 
 * Provides consistent entrance/exit animations for modal content.
 * Includes scale, fade, and slide effects for a polished appearance.
 * 
 * @component
 * @example
 * ```jsx
 * import { ModalBackdrop, ModalContent } from "../ui/Motion";
 * import { AnimatePresence } from "framer-motion";
 * 
 * <AnimatePresence>
 *   {isOpen && (
 *     <>
 *       <ModalBackdrop onClick={handleClose} />
 *       <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
 *         <ModalContent className="w-full max-w-md">
 *           <div className="bg-white rounded-2xl p-6">
 *             Modal content here
 *           </div>
 *         </ModalContent>
 *       </div>
 *     </>
 *   )}
 * </AnimatePresence>
 * ```
 */
export const ModalContent = ({
    children,
    className,
    style,
    ...props
}) => (
    <motion.div
        variants={MODAL_VARIANTS.content}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.2, ease: "easeOut" }}
        className={className}
        style={style}
        {...props}
    >
        {children}
    </motion.div>
);

ModalContent.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    style: PropTypes.object
};

export default ModalContent;
